/**
 * Default Radius Tokens
 *
 * This module defines the default border radius scale for the EroChat Design System.
 * Includes both scale-based values and semantic aliases for component consistency.
 *
 * @module design-system/tokens/radius
 */

import type { RadiusTokens } from './types';

/**
 * Default border radius scale
 *
 * The scale follows a progressive pattern for visual consistency:
 * - none: Sharp corners (0)
 * - sm: Subtle rounding (4px)
 * - md: Medium rounding (8px)
 * - lg: Large rounding (12px)
 * - xl: Extra large rounding (16px)
 * - 2xl: Maximum standard rounding (24px)
 * - full: Pill/circle shape (9999px)
 *
 * Semantic aliases map to scale values for component-specific usage,
 * ensuring consistency across similar UI elements.
 *
 * @example
 * ```tsx
 * import { defaultRadius } from '@/design-system/tokens/radius';
 *
 * // Use in inline styles
 * <button style={{ borderRadius: defaultRadius.button }} />
 *
 * // Use with CSS variables
 * document.documentElement.style.setProperty('--radius-card', defaultRadius.card);
 * ```
 */
export const defaultRadius: RadiusTokens = {
  // Scale-based values
  /** 0 - No border radius, sharp corners */
  none: '0',

  /** 4px / 0.25rem - Subtle rounding */
  sm: '0.25rem',

  /** 8px / 0.5rem - Medium rounding */
  md: '0.5rem',

  /** 12px / 0.75rem - Large rounding */
  lg: '0.75rem',

  /** 16px / 1rem - Extra large rounding */
  xl: '1rem',

  /** 24px / 1.5rem - 2x extra large rounding */
  '2xl': '1.5rem',

  /** 9999px - Full/pill shape for circular elements */
  full: '9999px',

  // Semantic aliases
  /** Button radius - maps to md for balanced appearance */
  button: '0.5rem',

  /** Card radius - maps to lg for prominent containers */
  card: '0.75rem',

  /** Input radius - maps to sm for subtle rounding */
  input: '0.25rem',

  /** Modal radius - maps to xl for prominent dialogs */
  modal: '1rem',

  /** Avatar radius - maps to full for circular avatars */
  avatar: '9999px',

  /** Badge radius - maps to full for pill shape */
  badge: '9999px',

  /** Tooltip radius - maps to md */
  tooltip: '0.5rem',

  /** Dropdown radius - maps to lg */
  dropdown: '0.75rem',
} as const;

/**
 * Radius scale in pixels (for reference and calculations)
 *
 * Use this when you need pixel values for calculations,
 * but always convert to rem for final CSS output.
 */
export const radiusPx = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 24,
  full: 9999,
  // Semantic values
  button: 8,
  card: 12,
  input: 4,
  modal: 16,
  avatar: 9999,
  badge: 9999,
  tooltip: 8,
  dropdown: 12,
} as const;

/**
 * Scale-based radius keys (without semantic aliases)
 */
export const radiusScaleKeys = ['none', 'sm', 'md', 'lg', 'xl', '2xl', 'full'] as const;

/**
 * Semantic alias keys
 */
export const radiusAliasKeys = ['button', 'card', 'input', 'modal', 'avatar', 'badge', 'tooltip', 'dropdown'] as const;

/**
 * All radius token keys
 */
export const radiusKeys = [...radiusScaleKeys, ...radiusAliasKeys] as const;

/**
 * Type for scale-based radius keys
 */
export type RadiusScaleKey = (typeof radiusScaleKeys)[number];

/**
 * Type for semantic alias keys
 */
export type RadiusAliasKey = (typeof radiusAliasKeys)[number];

/**
 * Type for all radius keys
 */
export type RadiusKey = (typeof radiusKeys)[number];

/**
 * CSS variable names for radius tokens
 *
 * These map to the CSS custom properties that will be injected into the DOM.
 */
export const radiusCSSVariables = {
  none: '--radius-none',
  sm: '--radius-sm',
  md: '--radius-md',
  lg: '--radius-lg',
  xl: '--radius-xl',
  '2xl': '--radius-2xl',
  full: '--radius-full',
  button: '--radius-button',
  card: '--radius-card',
  input: '--radius-input',
  modal: '--radius-modal',
  avatar: '--radius-avatar',
  badge: '--radius-badge',
  tooltip: '--radius-tooltip',
  dropdown: '--radius-dropdown',
} as const;

/**
 * Generate CSS variables map for radius tokens
 *
 * @param radius - Radius tokens to convert
 * @returns Object mapping CSS variable names to values
 *
 * @example
 * ```ts
 * const cssVars = getRadiusCSSVariables(defaultRadius);
 * // { '--radius-none': '0', '--radius-sm': '0.25rem', ... }
 * ```
 */
export function getRadiusCSSVariables(radius: RadiusTokens = defaultRadius): Record<string, string> {
  return {
    '--radius-none': radius.none,
    '--radius-sm': radius.sm,
    '--radius-md': radius.md,
    '--radius-lg': radius.lg,
    '--radius-xl': radius.xl,
    '--radius-2xl': radius['2xl'],
    '--radius-full': radius.full,
    '--radius-button': radius.button,
    '--radius-card': radius.card,
    '--radius-input': radius.input,
    '--radius-modal': radius.modal,
    '--radius-avatar': radius.avatar,
    '--radius-badge': radius.badge,
    '--radius-tooltip': radius.tooltip,
    '--radius-dropdown': radius.dropdown,
  };
}

/**
 * Mapping of semantic aliases to their scale values
 *
 * This can be customized per preset to change the relationship
 * between semantic names and actual values.
 */
export const defaultRadiusAliasMapping: Record<RadiusAliasKey, RadiusScaleKey> = {
  button: 'md',
  card: 'lg',
  input: 'sm',
  modal: 'xl',
  avatar: 'full',
  badge: 'full',
  tooltip: 'md',
  dropdown: 'lg',
} as const;
